import toast from 'react-hot-toast';
import { API_AuthAndRedirectChecker, ApiResponse, getData, postJson } from './Apis';

export type PositionsFeaturesModel = {
    JobBoard_GenerateLocationLinks: boolean,
    JobBoard_GenerateDepartmentLinks: boolean,
    JobBoard_GenerateRoleLinks: boolean,
    JobBoard_GenerateCategoryLinks: boolean,
    JobBoard_ShowExperience: boolean,
    JobBoard_ShowPQE: boolean,
    JobBoard_ShowRef: boolean,
    JobBoard_PracticeGroup: boolean,
    DisplayText_Department: string,
    DisplayText_PracticeGroup: string,
}

export type PositionsModel = {
    Roles: string[],
    Departments: string[],
    Locations: string[],
    Features: PositionsFeaturesModel,
    SettingLinks: PositionLinksModel[],
    Positions: PositionDetailsModel[],
    JobCategories: JobCategoryModel[],
}

export type PositionDetailsModel = {
    CandidateTitle: string,
    ExternalDepartment: string,
    PracticeGroup: string,
    LocationText: string,
    PQERequired: string,
    ExperienceLevel: string,
    ApplyingForID: number,
    LocationOrder: number,
    DepartmentOrder: number,
    RoleOrder: number,
    CategoryCode: string,
}

export type PositionLinksModel = {
    Name: string,
    URL: string,
}

export type JobCategoryModel = {
    Name: string,
    Code: string,
}

export async function API_GetPositionOverview(intranetToken: string, intranetReq: boolean) {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const token = tokens && tokens.length ? tokens[0].value : '';
    const response = await fetch(`${window.location.origin}/webapi/candidate/Positions?isIntranetReq=${intranetReq}${intranetToken != null && intranetToken.length > 0 ? "&token=" + intranetToken : ""}`, {
        method: "GET",
        credentials: "same-origin",
        headers: {
            'Accepts': 'application/json',
            '__RequestVerificationToken': token,
        }
    });

    if (response.ok) {
        return await response.json() as ApiResponse<PositionsModel>;
    }

    toast.error("Error");
    throw new Error('Unexpected error');
}

export type PositionContainerModel = {
    Details: PositionModel,
    CanApply: boolean,
    CanForwardToFriend: boolean,
    Check: PositionCheck,
    Features: PositionDisplayFeaturesModel,
    Sections: PositionSectionModel[],
    ExistingApplications: ExistingApplicationModel[],
}

export type PositionCheck = {
    Details: PositionCheckState,
    PositionStatus: string
}

export type ExistingApplicationModelContainer = {
    Existing: ExistingApplicationModel[],
    ApplyingForID: number,
    ApplyingFor: string
}

export type ExistingApplicationModel = {
    ApplicationNumber: number,
    ApplyingForID: number,
    ApplyingFor: string
}

export type PositionCheckState = {
    DateConfirmed?: Date,
    ApplicationNumber?: number
}

export type PositionModel = {
    ClosingDate: string,
    PQERequired: string,
    ExperienceLevel: string,
    TermType: string,
    Duration: string,
    Salary: string,
    Fulltime: string,
    JobHours: string,
    ReportsTo: string,
    JobRef: string,
    ExternalDepartment: string,
    LocationText: string,
    Specialism: string,
    HiringManagerName: string,
}

export type PositionSectionModel = {
    SectionHeader: string,
    SectionText: string,
}

export type PositionDisplayFeaturesModel = {
    Positions_PQE_ShowExternally: boolean,
    Positions_Salary_ShowExternally: boolean,
    Intranet_HMN_ShowExternally: boolean,
    Intranet_ShortForm: boolean,
}

export async function API_GetPositionDetails(id: string, jb: string, ddsb: string, aref: string, intranetToken: string, intranetReq: boolean): Promise<ApiResponse<PositionContainerModel>> {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const token = tokens && tokens.length ? tokens[0].value : '';
    const response = await fetch(`${window.location.origin}/webapi/candidate/position/details/?id=${id}&isIntranetReq=${intranetReq}${jb != null && jb != '' ? ("&jb=" + jb) : ""}${ddsb != null && ddsb != '' ? ("&ddsb=" + ddsb) : ""}${aref != null && aref != '' ? ("&aref=" + aref) : ""}${intranetToken != null && intranetToken != '' ? ("&token=" + intranetToken) : ""}`, {
        method: "GET",
        credentials: "same-origin",
        headers: {
            'Accepts': 'application/json',
            '__RequestVerificationToken': token,
        }
    });

    if (await API_AuthAndRedirectChecker(response)) {
        return new Promise((x, y) => { }); //Wait for page load
    }

    if (response.ok) {
        var jsn = await response.json();
        return jsn as ApiResponse<PositionContainerModel>;
    }

    toast.error("Error");
    throw new Error('Unexpected error');
}

export type PositionCreateOrRedirectModel = {
    RefID: number,
    Aref: string,
    ExistingToCopyID: number | null,
    returl: string,
    Token: string,
    Name: string,
    IntranetReq: boolean,
}

export async function API_PositionCreateOrRedirectCheck(data: PositionCreateOrRedirectModel): Promise<ApiResponse<{}>> {
    return await postJson<ApiResponse<{}>>("webapi/candidate/position/applyorredirect", data);
}

export type PositionForwardModel = {
    ID: number,
    Email: string,
    FirstName: string,
    LastName: string,
    FriendEmail: string,
    FriendFirstName: string,
    FriendLastName: string,
    Comment: string,
    Captcha: string,
}

export async function API_PositionForwardToFriend(data: PositionForwardModel): Promise<ApiResponse<{}>> {
    return await postJson<ApiResponse<{}>>("webapi/candidate/position/forward", data);
}

export async function API_GetApplicantExisting(aref: string): Promise<ApiResponse<ExistingApplicationModel[]>> {
    return await getData<ApiResponse<ExistingApplicationModel[]>>(`webapi/candidate/position/getexisting?aref=${aref}`);
}

export type IntranetPositionDetailsModel = {
    PersonalDetails_TitleExamples: string,
    Intranet_CurrentManagerAware_Label: string,
    IntranetShortForm_CurrentManagerAware: boolean,
    IntranetShortForm_CurrentJobTitle: boolean,
    Intranet_CoverLetterOption: boolean,
    Intranet_CurrentSalary: boolean,
    Intranet_CurrentSalary_Required: boolean,
    Intranet_DesiredSalary: boolean,
    Intranet_DesiredSalary_Required: boolean,
    Intranet_PassedProbationPeriod: boolean,
    Intranet_PassedProbationPeriod_Required: boolean,
    Intranet_CurrentJobTitle_Required: boolean,
    Intranet_CurrentManagerAware_Required: boolean,
    Intranet_Title: boolean,
    Intranet_Title_Required: boolean,
    PositionName: string,
}

export async function API_GetIntranetQuickApply(id: string, intranetToken: string): Promise<ApiResponse<IntranetPositionDetailsModel>> {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const token = tokens && tokens.length ? tokens[0].value : '';
    const response = await fetch(`${window.location.origin}/webapi/candidate/position/intranet/quickapply/?id=${id}${intranetToken != null && intranetToken != '' ? ("&token=" + intranetToken) : ""}`, {
        method: "GET",
        credentials: "same-origin",
        headers: {
            'Accepts': 'application/json',
            '__RequestVerificationToken': token,
        }
    });

    if (await API_AuthAndRedirectChecker(response)) {
        return new Promise((x, y) => { }); //Wait for page load
    }

    if (response.ok) {
        var jsn = await response.json();
        return jsn as ApiResponse<IntranetPositionDetailsModel>;
    }

    toast.error("Error");
    throw new Error('Unexpected error');
}

export type IntranetPositionPostModel = {
    Title: string,
    Forenames: string,
    Surname: string,
    EmailAddress: string,
    CurrentManagerAware?: boolean,
    CVFileName: string,
    CoverLetterFileName: string,
    CurrentJobTitle: string,
    CurrentSalary: string,
    DesiredSalary: string,
    PassedProbationPeriod: string,
}

export async function API_IntranetQuickApply(data: FormData, id : string, token : string): Promise<ApiResponse<IntranetPositionPostResponseModel>> {
    const tokens: NodeListOf<HTMLInputElement> = document.getElementsByName('__RequestVerificationToken') as any;
    const csrfToken = tokens && tokens.length ? tokens[0].value : '';

    const response = await fetch(`${window.location.origin}${`/webapi/candidate/position/intranet/quickapply?id=${id}&token=${token}`}`, {
        method: "POST",
        headers: { __RequestVerificationToken: csrfToken, Accept: 'application/json' },
        body: data,
    });
    
    if (response.ok) {
        var jsn = await response.json();
        return jsn as ApiResponse<IntranetPositionPostResponseModel>;
    }
    
    toast.error("Error");
    throw new Error('Unexpected error');
}

export type IntranetPositionPostResponseModel = {
    Errors: string[],
}